import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Select, FormControl, InputLabel,
  Alert, Tooltip, Skeleton, Checkbox, FormControlLabel,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import VpnKeyIcon from '@mui/icons-material/VpnKeyOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { useAuth } from '../../auth/AuthContext';
import ApiErrorAlert from '../../components/ApiErrorAlert';
import {
  listProviderConfigs, createProviderConfig, updateProviderConfig, deleteProviderConfig,
  getByoWaivers, signByoWaiver, revokeByoWaiver, attestProviderConfig,
} from '../../api/developerApi';

const PROVIDER_TYPES = [
  { value: 'OPENAI_COMPATIBLE', label: 'OpenAI-compatible endpoint' },
  { value: 'ANTHROPIC_NATIVE', label: 'Anthropic API' },
];

// What a provider alias may be when it is introduced or changed. The server enforces the same rule and leaves an
// alias that a config already holds alone, so the edit form lets an unchanged alias through.
const ALIAS_FORMAT = /^[a-z0-9_-]{1,64}$/;
const ALIAS_RULE = 'Use 1 to 64 lowercase letters, digits, - or _.';

// What an owner of an account that holds a Business Associate Agreement with Vectros attests, per provider config.
// It is the sentence the waiver quotes; the server records its own copy of it.
export const BAA_ATTESTATION =
  'I attest that this account holds a business associate agreement, or another agreement that HIPAA requires, '
  + 'with the provider at the endpoint on this configuration, covering the information this account will send to it, '
  + 'and that this account is permitted to direct that disclosure.';

// What signing the waiver states: the same for every account.
export const WAIVER_SIGNING_STATEMENT =
  'I am an owner of this account and I am authorized to accept this waiver for this tenant. I have read it. '
  + 'I direct Vectros to send requests that select a provider I configure to that provider, at the endpoint address '
  + 'I save, and I am responsible for that address being correct. I understand that those requests leave the Vectros '
  + 'perimeter and are governed by my provider. I accept responsibility for my provider account\'s terms and charges; '
  + 'for how my provider processes, stores, logs, retains, shares and secures what it receives, and where; for any '
  + 'incident at or by my provider and any notification it requires; and for deciding what data I send and that '
  + 'sending it is lawful.';

// The host of an endpoint, lower-cased the way the server compares it; null when it is not an http(s) URL.
function hostOf(endpoint) {
  try {
    const url = new URL(endpoint);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.hostname.toLowerCase() : null;
  } catch (err) {
    return null;
  }
}

// effectiveState values come from the API, which decides whether a config is usable. The page only
// labels them; it never re-derives entitlement.
const STATE_STYLES = {
  ACTIVE:           { label: 'Active',          bgcolor: '#dcfce7', color: '#166534' },
  DISABLED:         { label: 'Disabled',        bgcolor: '#f4f4f5', color: '#52525b' },
  WAIVER_REQUIRED:  { label: 'Waiver required', bgcolor: '#fef9c3', color: '#854d0e' },
  WAIVER_STALE:     { label: 'Sign again',      bgcolor: '#fef9c3', color: '#854d0e' },
  BAA_ATTESTATION_REQUIRED: { label: 'Attestation required', bgcolor: '#fef9c3', color: '#854d0e' },
};

const WAIVER_STATE_LABEL = {
  ACTIVE: 'Signed',
  OPERATOR_GRANTED: 'Granted by Vectros',
  NOT_SIGNED: 'Not signed',
  STALE: 'The text changed: sign again',
  REVOKED: 'Revoked',
};

// A failed request keeps the request body on its error object (config.data). For create and rotate that body
// holds the provider key, so drop it before the error is stored, shown or logged.
function withoutRequestBody(err) {
  if (err && err.config) err.config.data = undefined;
  throw err;
}

const buttonSx = { bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 };

function StateChip({ state }) {
  const s = STATE_STYLES[state] || STATE_STYLES.DISABLED;
  return <Chip label={s.label} size="small" sx={{ bgcolor: s.bgcolor, color: s.color, fontWeight: 600, fontSize: 11 }} />;
}

// ---------------------------------------------------------------------------
// Waiver card + sign dialog
// ---------------------------------------------------------------------------

function WaiverSection({ env, waiver, loading, onChanged, switchedOffCount }) {
  const [signOpen, setSignOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [showExact, setShowExact] = useState(false);

  const signMutation = useMutation(
    () => signByoWaiver(env, {
      waiverType: waiver.waiverType,
      documentHash: waiver.documentHash,
      acknowledged: true,
    }),
    {
      onSuccess: () => { setSignOpen(false); setAcknowledged(false); onChanged(); },
      // A 409 means the text changed after it was shown: fetch the current text and hash so a retry signs what is
      // shown, and clear the ticked box, because it was given for the text that was shown before.
      onError: () => { setAcknowledged(false); onChanged(); },
    },
  );

  // If the text on screen changes under an open dialog (a refetch), earlier acknowledgements no longer apply to it.
  const shownHash = waiver ? waiver.documentHash : null;
  useEffect(() => { setAcknowledged(false); }, [shownHash]);
  const revokeMutation = useMutation(
    () => revokeByoWaiver(env, waiver.waiverType),
    { onSuccess: () => { setRevokeOpen(false); onChanged(); } },
  );

  if (loading || !waiver) return <Skeleton variant="rounded" height={88} sx={{ mb: 3 }} />;

  const granted = waiver.granted;
  const openSign = () => {
    signMutation.reset(); setAcknowledged(false); setShowExact(false); setSignOpen(true);
  };

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, p: 2.5, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ maxWidth: 640 }}>
          <Typography variant="subtitle1" fontWeight={700}>Provider risk waiver</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            A call routed to a provider you configure is served by that provider and leaves the Vectros
            perimeter. An account owner must accept the waiver for this environment before a provider config
            can be used.
          </Typography>
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip size="small" label={WAIVER_STATE_LABEL[waiver.state] || waiver.state}
              sx={{ fontWeight: 600, fontSize: 11, bgcolor: granted ? '#dcfce7' : '#fef9c3', color: granted ? '#166534' : '#854d0e' }} />
            {waiver.signedAt && (
              <Typography variant="caption" color="text.secondary">
                {waiver.revokedAt ? 'Revoked ' + new Date(waiver.revokedAt).toLocaleString()
                  : 'Signed ' + new Date(waiver.signedAt).toLocaleString()}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {granted
            ? <Button variant="outlined" color="error" onClick={() => { revokeMutation.reset(); setRevokeOpen(true); }}
                sx={{ textTransform: 'none' }}>Revoke waiver</Button>
            : <Button variant="contained" onClick={openSign} sx={buttonSx}>Review and sign</Button>}
        </Box>
      </Box>

      {granted && switchedOffCount > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {switchedOffCount} provider{switchedOffCount > 1 ? 's are' : ' is'} switched off. Use the power button on
          the row to switch {switchedOffCount > 1 ? 'them' : 'it'} on.
        </Alert>
      )}

      {/* Sign dialog: shows exactly the text the API returned, and sends its hash back. */}
      <Dialog open={signOpen} onClose={() => setSignOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Provider risk waiver</DialogTitle>
        <DialogContent>
          <ApiErrorAlert error={signMutation.error} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {showExact
                ? 'This is the exact text that is recorded when you sign.'
                : 'Formatted for reading. The exact text that is recorded when you sign is one click away.'}
            </Typography>
            <Button size="small" onClick={() => setShowExact((v) => !v)} sx={{ textTransform: 'none' }}>
              {showExact ? 'View formatted text' : 'View exact signed text'}
            </Button>
          </Box>
          {showExact ? (
            <>
              <Box component="pre" data-testid="waiver-text" tabIndex={0} role="region" aria-label="Exact signed waiver text"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13, m: 0, p: 2, maxHeight: 360, overflowY: 'auto',
                  border: '1px solid #E4E4E7', borderRadius: 1, bgcolor: 'background.default' }}>
                {waiver.documentText}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" data-testid="waiver-hash"
                sx={{ mt: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                Document hash: {waiver.documentHash}
              </Typography>
            </>
          ) : (
            <Box data-testid="waiver-rendered" tabIndex={0} role="region" aria-label="Waiver text"
              sx={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #E4E4E7', borderRadius: 1, bgcolor: 'background.default', p: 2,
                '& h1,h2,h3': { fontWeight: 700, mt: 2, mb: 1 },
                '& h1': { fontSize: 20, mt: 0 }, '& h2': { fontSize: 16 }, '& h3': { fontSize: 14 },
                '& p': { mb: 1.5, lineHeight: 1.6, fontSize: 13 },
                '& ul,ol': { pl: 3, mb: 1.5 },
                '& li': { fontSize: 13, mb: 0.5 },
                '& blockquote': { m: 0, mb: 1.5, pl: 2, borderLeft: '3px solid #E4E4E7', color: 'text.secondary' },
                '& code': { fontFamily: 'monospace', fontSize: 12 } }}>
              <ReactMarkdown>{waiver.documentText}</ReactMarkdown>
            </Box>
          )}
          <FormControlLabel sx={{ mt: 2 }}
            control={<Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
            label={WAIVER_SIGNING_STATEMENT} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSignOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={!acknowledged || signMutation.isLoading}
            onClick={() => signMutation.mutate()} sx={buttonSx}>
            {signMutation.isLoading ? 'Signing…' : 'Sign waiver'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revokeOpen} onClose={() => setRevokeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Revoke the waiver?</DialogTitle>
        <DialogContent>
          <ApiErrorAlert error={revokeMutation.error} sx={{ mb: 2 }} />
          <Typography variant="body2">
            Routed calls to your providers are refused once the change takes effect, normally within moments,
            and the provider configs that depend on this waiver are switched off. Your earlier signature stays
            on record. You can sign again later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" disabled={revokeMutation.isLoading}
            onClick={() => revokeMutation.mutate()} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {revokeMutation.isLoading ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ProvidersPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [env, setEnv] = useState('test');
  const [createOpen, setCreateOpen] = useState(false);
  const [rotateTarget, setRotateTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [attestTarget, setAttestTarget] = useState(null);
  // The destination (provider type + endpoint) the box was ticked for, or null. A tick is for the destination that was on screen when it
  // was given: if the list later shows another one (another session edited the config), the tick no longer counts.
  const [attestTicked, setAttestTicked] = useState(null);

  const tenantId = env === 'live' ? user?.liveTenantId : user?.testTenantId;

  const waiversQuery = useQuery(['byo-waivers', env], () => getByoWaivers(env), { enabled: !!tenantId });
  const configsQuery = useQuery(['provider-configs', tenantId], () => listProviderConfigs(tenantId), { enabled: !!tenantId });
  const waiver = waiversQuery.data?.waivers?.[0];
  const baaHolder = !!waiversQuery.data?.baaHolder;
  const configs = configsQuery.data || [];
  // The attest dialog shows, and attests, the config as the list holds it now: after a 409 the list is read again and the
  // dialog moves to the destination the config has, rather than keeping the one that was shown when it opened.
  const attestRow = attestTarget ? (configs.find((c) => c.id === attestTarget.id) || attestTarget) : null;
  const attestChecked = !!attestTicked && !!attestRow && attestTicked.providerType === attestRow.providerType
    && attestTicked.endpoint === (attestRow.endpoint || '');

  const refresh = () => {
    qc.invalidateQueries(['byo-waivers', env]);
    qc.invalidateQueries(['provider-configs', tenantId]);
  };

  const createForm = useForm({ defaultValues: { alias: '', providerType: 'OPENAI_COMPATIBLE', endpoint: '', defaultProviderModelId: '', apiKeySecret: '' } });
  const rotateForm = useForm({ defaultValues: { apiKeySecret: '' } });
  const editForm = useForm({ defaultValues: { alias: '', providerType: 'OPENAI_COMPATIBLE', endpoint: '', defaultProviderModelId: '', apiKeySecret: '' } });
  const providerType = createForm.watch('providerType');

  // What the Edit dialog would send: only the fields that differ from the stored config, so a save never restates
  // (and so never re-judges) a value the config already holds.
  const editValues = editForm.watch();
  const editChanges = {};
  // The stored key is only ever used for the host it was entered for. Whenever the edit moves the config to another
  // host (a different endpoint host, or leaving a stored endpoint for the Anthropic API) the key is asked for again.
  let editNeedsKey = false;
  let editKeyHost = null;
  if (editTarget) {
    const rawAlias = editValues.alias || '';
    const alias = rawAlias.trim();
    if (rawAlias !== editTarget.alias && alias !== editTarget.alias) editChanges.alias = alias;
    if (editValues.providerType !== editTarget.providerType) editChanges.providerType = editValues.providerType;
    if (editValues.providerType === 'OPENAI_COMPATIBLE') {
      const endpoint = (editValues.endpoint || '').trim();
      // The platform refuses a type change that does not state the endpoint, so it is sent whenever the type changes,
      // even when the prefilled value is the stored one (an Anthropic config that carries an endpoint override).
      if (endpoint !== (editTarget.endpoint || '') || editValues.providerType !== editTarget.providerType) {
        editChanges.endpoint = endpoint;
        // A value that is not a URL is refused by the form, so a host that cannot be read never reaches this check.
        const newHost = hostOf(endpoint);
        editNeedsKey = newHost !== null && newHost !== hostOf(editTarget.endpoint || '');
        editKeyHost = editNeedsKey ? newHost : null;
      }
    } else if (editValues.providerType !== editTarget.providerType && editTarget.endpoint) {
      // Any stored endpoint overrides the Anthropic default at call time, so leaving it would keep sending calls to the
      // old host. Clear it; the key then moves to the Anthropic API, so it is asked for again.
      editChanges.endpoint = '';
      editNeedsKey = true;
      editKeyHost = '';
    }
    const model = (editValues.defaultProviderModelId || '').trim();
    if (model !== (editTarget.defaultProviderModelId || '')) editChanges.defaultProviderModelId = model;
  }
  const editAliasChanged = 'alias' in editChanges;
  // A key typed for one host must not follow the endpoint to another, or linger once none is needed.
  useEffect(() => {
    if (editForm.getValues('apiKeySecret')) editForm.setValue('apiKeySecret', '');
  }, [editKeyHost]); // eslint-disable-line react-hooks/exhaustive-deps

  // reset() on success drops the mutation's retained variables, which hold the key that was just sent.
  const createMutation = useMutation((body) => createProviderConfig(body).catch(withoutRequestBody), {
    onSuccess: () => { setCreateOpen(false); createForm.reset(); createMutation.reset(); refresh(); },
  });
  // The list is read through an index that lags a write by about a second, and is cached for 30 s, so a refetch right after
  // a write can return the row as it was. The write's own response is the consistent row: put it in the list.
  const putRowInList = (row) => {
    if (!row || !row.id) { qc.invalidateQueries(['provider-configs', tenantId]); return; } // no row to put: read the list again
    qc.setQueryData(['provider-configs', tenantId], (old) => (Array.isArray(old) ? old.map((c) => (c.id === row.id ? row : c)) : old));
  };
  const updateMutation = useMutation(({ id, body }) => updateProviderConfig(id, body).catch(withoutRequestBody), {
    // The dialogs close themselves from the call that submitted them, so a toggle that finishes while a dialog is open
    // cannot close it.
    onSuccess: (row) => { putRowInList(row); qc.invalidateQueries(['byo-waivers', env]); },
  });
  // What is sent is the destination the box was TICKED for, never a newer one the list has since shown: the server then still
  // answers 409 if the config moved in between.
  const attestMutation = useMutation((target) => attestProviderConfig(target.id, {
    providerType: target.providerType, endpoint: target.endpoint || '',
  }), {
    onSuccess: (row) => { setAttestTarget(null); setAttestTicked(null); putRowInList(row); qc.invalidateQueries(['byo-waivers', env]); },
    // A 409 means the config holds another destination than the one shown: the server names it. Read the list again and clear
    // the tick, so the owner reads the new destination and ticks again before anything is recorded for it.
    onError: () => { setAttestTicked(null); refresh(); },
  });
  const closeAttest = () => { setAttestTarget(null); setAttestTicked(null); attestMutation.reset(); };
  const closeCreate = () => { setCreateOpen(false); createForm.reset(); createMutation.reset(); };
  const closeRotate = () => { setRotateTarget(null); rotateForm.reset(); updateMutation.reset(); };
  const closeEdit = () => { setEditTarget(null); editForm.reset(); updateMutation.reset(); };
  const openEdit = (c) => {
    updateMutation.reset();
    editForm.reset({
      alias: c.alias, providerType: c.providerType, endpoint: c.endpoint || '',
      defaultProviderModelId: c.defaultProviderModelId || '', apiKeySecret: '',
    });
    setEditTarget(c);
  };
  const deleteMutation = useMutation(deleteProviderConfig, {
    onSuccess: () => { setDeleteTarget(null); refresh(); },
  });

  const onCreate = (v) => {
    const body = {
      tenantId,
      alias: v.alias.trim(),
      providerType: v.providerType,
      apiKeySecret: v.apiKeySecret,
      // The waiver is per environment: a config is created switched on only when it is already signed.
      status: waiver?.granted ? 'ACTIVE' : 'DISABLED',
    };
    if (v.providerType === 'OPENAI_COMPATIBLE') body.endpoint = v.endpoint.trim();
    if (v.defaultProviderModelId.trim()) body.defaultProviderModelId = v.defaultProviderModelId.trim();
    createMutation.mutate(body);
  };

  const onEdit = (v) => {
    if (Object.keys(editChanges).length === 0) return;
    // The key goes along only when the endpoint host changed; it is never read back from the config.
    updateMutation.mutate(
      { id: editTarget.id, body: { ...editChanges, ...(editNeedsKey ? { apiKeySecret: v.apiKeySecret } : {}) } },
      { onSuccess: closeEdit },
    );
  };

  const onRotate = (v) => updateMutation.mutate({ id: rotateTarget.id, body: { apiKeySecret: v.apiKeySecret } }, { onSuccess: closeRotate });

  const toggle = (c) => updateMutation.mutate({ id: c.id, body: { status: c.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' } });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Model providers</Typography>
          <Typography variant="body2" color="text.secondary">
            Route chat, RAG and document-ask calls to a model you host or pay for, with <code>providerAlias</code>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel id="providers-env-label">Environment</InputLabel>
            <Select labelId="providers-env-label" value={env} label="Environment" onChange={(e) => setEnv(e.target.value)}>
              <MenuItem value="live">Live</MenuItem>
              <MenuItem value="test">Test</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} sx={buttonSx}
            onClick={() => { createMutation.reset(); setCreateOpen(true); }}>
            Add provider
          </Button>
        </Box>
      </Box>

      <ApiErrorAlert error={waiversQuery.error || configsQuery.error} sx={{ mb: 2 }} />
      <WaiverSection env={env} waiver={waiver} loading={waiversQuery.isLoading} onChanged={refresh}
        switchedOffCount={configs.filter((c) => c.status !== 'ACTIVE').length} />

      <ApiErrorAlert
        error={(!rotateTarget && !editTarget && updateMutation.error) || (!deleteTarget && deleteMutation.error) || null}
        onClose={() => { updateMutation.reset(); deleteMutation.reset(); }}
        sx={{ mb: 2 }}
      />

      <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Alias</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Provider</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Default model</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>State</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {configsQuery.isLoading
              ? Array.from({ length: 2 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 5 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
              ))
              : configs.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{c.alias}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {c.providerType === 'ANTHROPIC_NATIVE' ? 'Anthropic API' : 'OpenAI-compatible'}
                    {c.endpoint && (
                      <Typography variant="caption" display="block" color="text.secondary"
                        sx={{ fontFamily: 'monospace', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.endpoint}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{c.defaultProviderModelId || 'None'}</TableCell>
                  <TableCell>
                    <StateChip state={c.effectiveState} />
                    {c.effectiveState !== 'ACTIVE' && c.reason && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: 11, mt: 0.5 }}>
                        {c.reason}
                      </Typography>
                    )}
                    {baaHolder && c.baaAttested && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: 11, mt: 0.5 }}>
                        BAA attested{c.baaAttestedAt ? ' ' + new Date(c.baaAttestedAt).toLocaleDateString() : ''}
                      </Typography>
                    )}
                    {baaHolder && !c.baaAttested && (
                      <Button size="small" sx={{ textTransform: 'none', mt: 0.5, p: 0, minWidth: 0 }}
                        aria-label={'Attest your BAA with this provider ' + c.alias}
                        onClick={() => { attestMutation.reset(); setAttestTicked(null); setAttestTarget(c); }}>
                        Attest your BAA with this provider
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={c.status === 'ACTIVE' ? 'Switch off' : 'Switch on'}>
                      <IconButton size="small" aria-label={(c.status === 'ACTIVE' ? 'Disable provider ' : 'Enable provider ') + c.alias}
                        disabled={updateMutation.isLoading} onClick={() => toggle(c)}>
                        <PowerSettingsNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" aria-label={'Edit provider ' + c.alias}
                        onClick={() => openEdit(c)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Rotate key">
                      <IconButton size="small" aria-label={'Rotate provider key ' + c.alias}
                        onClick={() => { updateMutation.reset(); rotateForm.reset(); setRotateTarget(c); }}>
                        <VpnKeyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" aria-label={'Delete provider ' + c.alias}
                        onClick={() => { deleteMutation.reset(); setDeleteTarget(c); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            {!configsQuery.isLoading && configs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2">No providers yet.</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add a provider, sign the waiver, switch the provider on, then send <code>providerAlias</code> on a chat, RAG or document-ask call.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create */}
      <Dialog open={createOpen} onClose={closeCreate} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add provider</DialogTitle>
        <DialogContent>
          <ApiErrorAlert error={createMutation.error} sx={{ mb: 2 }} />
          <Box component="form" id="provider-create-form" onSubmit={createForm.handleSubmit(onCreate)} noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Alias" size="small" fullWidth
              error={!!createForm.formState.errors.alias}
              helperText={createForm.formState.errors.alias ? ALIAS_RULE : 'The name you send as providerAlias. ' + ALIAS_RULE}
              {...createForm.register('alias', { validate: (v) => ALIAS_FORMAT.test(v.trim()) })} />
            <FormControl size="small" fullWidth>
              <InputLabel id="providers-type-label">Provider type</InputLabel>
              <Select labelId="providers-type-label" label="Provider type" value={providerType}
                onChange={(e) => createForm.setValue('providerType', e.target.value)}>
                {PROVIDER_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            {providerType === 'OPENAI_COMPATIBLE' && (
              <TextField label="Endpoint URL" size="small" fullWidth placeholder="https://models.example.org"
                helperText="https on port 443, without /v1. The path /v1/chat/completions is added for you."
                {...createForm.register('endpoint', { required: true })} />
            )}
            <TextField label="Default model" size="small" fullWidth
              helperText="Used when a request names no model. Optional."
              {...createForm.register('defaultProviderModelId')} />
            <TextField label="Provider API key" size="small" fullWidth type="password" autoComplete="off"
              helperText="Stored for this environment and never shown again."
              {...createForm.register('apiKeySecret', { required: true })} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCreate} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button type="submit" form="provider-create-form" variant="contained" disabled={createMutation.isLoading} sx={buttonSx}>
            {createMutation.isLoading ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editTarget} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit {editTarget?.alias}</DialogTitle>
        <DialogContent>
          <ApiErrorAlert error={editTarget ? updateMutation.error : null} sx={{ mb: 2 }} />
          <Box component="form" id="provider-edit-form" onSubmit={editForm.handleSubmit(onEdit)} noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Alias" size="small" fullWidth
              error={!!editForm.formState.errors.alias}
              helperText={editForm.formState.errors.alias ? ALIAS_RULE : 'The name you send as providerAlias. ' + ALIAS_RULE}
              {...editForm.register('alias', {
                validate: (v) => v === editTarget?.alias || v.trim() === editTarget?.alias || ALIAS_FORMAT.test(v.trim()),
              })} />
            {editAliasChanged && (
              <Alert severity="warning" data-testid="alias-change-warning">
                Callers that send <code>{editTarget?.alias}</code> as providerAlias stop working as soon as you save.
                Update them to the new alias.
              </Alert>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel id="providers-edit-type-label">Provider type</InputLabel>
              <Select labelId="providers-edit-type-label" label="Provider type" value={editValues.providerType}
                onChange={(ev) => editForm.setValue('providerType', ev.target.value)}>
                {PROVIDER_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            {baaHolder && editTarget?.baaAttested && ('endpoint' in editChanges || 'providerType' in editChanges) && (
              <Alert severity="warning" data-testid="attestation-withdrawn-note">
                This changes where calls are sent, so it withdraws your BAA attestation for this config. Calls are
                refused until an owner attests again.
              </Alert>
            )}
            {'providerType' in editChanges && !!(editValues.defaultProviderModelId || '').trim() && (
              <Alert severity="info" data-testid="default-model-kept-note">
                The default model <code>{(editValues.defaultProviderModelId || '').trim()}</code> is kept. Check that it
                names a model on the new provider, or change it below.
              </Alert>
            )}
            {editValues.providerType === 'OPENAI_COMPATIBLE' && (
              <TextField label="Endpoint URL" size="small" fullWidth placeholder="https://models.example.org"
                error={!!editForm.formState.errors.endpoint}
                helperText={editForm.formState.errors.endpoint
                  ? 'Enter the full URL, for example https://models.example.org'
                  : 'https on port 443, without /v1. The path /v1/chat/completions is added for you.'}
                {...editForm.register('endpoint', {
                  validate: (v) => {
                    const url = v.trim();
                    return url !== '' && (url === (editTarget?.endpoint || '') || hostOf(url) !== null);
                  },
                })} />
            )}
            <TextField label="Default model" size="small" fullWidth
              helperText="Used when a request names no model. Optional."
              {...editForm.register('defaultProviderModelId')} />
            {editNeedsKey && (
              <TextField label="Provider API key" size="small" fullWidth type="password" autoComplete="off"
                error={!!editForm.formState.errors.apiKeySecret}
                helperText="This moves the config to a different host, so the stored key is not reused. Enter the key for the new host."
                {...editForm.register('apiKeySecret', { validate: (v) => v.length > 0 })} />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEdit} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button type="submit" form="provider-edit-form" variant="contained"
            disabled={updateMutation.isLoading || Object.keys(editChanges).length === 0} sx={buttonSx}>
            {updateMutation.isLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* BAA-holder attestation, for one config and the destination it names now */}
      <Dialog open={!!attestTarget} onClose={closeAttest} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Attest for {attestRow?.alias}</DialogTitle>
        <DialogContent>
          <ApiErrorAlert error={attestTarget ? attestMutation.error : null} sx={{ mb: 2 }} />
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This account holds a Business Associate Agreement with Vectros. A call routed to a provider leaves
            that agreement, so Vectros routes calls through this config only after an owner makes the attestation
            below. It covers the endpoint shown here. Changing the endpoint or the provider type withdraws it.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">Endpoint on this configuration</Typography>
          <Typography variant="body2" data-testid="attest-endpoint" sx={{ fontFamily: 'monospace', mb: 2, wordBreak: 'break-all' }}>
            {attestRow?.endpoint || (attestRow?.providerType === 'ANTHROPIC_NATIVE' ? 'the Anthropic API (default)' : '')}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={attestChecked} onChange={(e) => setAttestTicked(e.target.checked
              ? { providerType: attestRow.providerType, endpoint: attestRow.endpoint || '' } : null)} />}
            label={BAA_ATTESTATION} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAttest} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={!attestChecked || attestMutation.isLoading}
            onClick={() => attestMutation.mutate({ id: attestRow.id, ...attestTicked })} sx={buttonSx}>
            {attestMutation.isLoading ? 'Recording…' : 'Record attestation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rotate key */}
      <Dialog open={!!rotateTarget} onClose={closeRotate} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Rotate key for {rotateTarget?.alias}</DialogTitle>
        <DialogContent>
          <ApiErrorAlert error={rotateTarget ? updateMutation.error : null} sx={{ mb: 2 }} />
          <Box component="form" id="provider-rotate-form" onSubmit={rotateForm.handleSubmit(onRotate)} noValidate sx={{ pt: 1 }}>
            <TextField label="New provider API key" size="small" fullWidth type="password" autoComplete="off"
              helperText="Replaces the stored key. The old key is not shown."
              {...rotateForm.register('apiKeySecret', { required: true })} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeRotate} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button type="submit" form="provider-rotate-form" variant="contained" disabled={updateMutation.isLoading} sx={buttonSx}>
            {updateMutation.isLoading ? 'Saving…' : 'Rotate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete {deleteTarget?.alias}?</DialogTitle>
        <DialogContent>
          <ApiErrorAlert error={deleteTarget ? deleteMutation.error : null} sx={{ mb: 2 }} />
          <Typography variant="body2">
            The config and its stored key are removed. Calls that send this providerAlias will be refused.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" disabled={deleteMutation.isLoading}
            onClick={() => deleteMutation.mutate(deleteTarget.id)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {deleteMutation.isLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
